/**
 * Authentication Provider and Context
 *
 * Provides authentication state and methods throughout the app.
 * Wraps the useFirebaseAuth hook and exposes it via React Context.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { User, UserCredential } from 'firebase/auth';
import { useFirebaseAuth } from '@/features/auth/hooks/useFirebaseAuth';
import type { UserProfile } from '@/shared/types';

/**
 * Authentication Context Type
 */
interface AuthContextType {
  // Auth state
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  authenticated: boolean;

  // Auth methods
  login: (email: string, password: string) => Promise<UserCredential>;
  register: (email: string, password: string, displayName: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  // Utility methods
  clearError: () => void;
}

/**
 * Create Auth Context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider Component
 *
 * Wraps the application and provides auth state via context.
 * Must be rendered at the root of the app.
 */
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authHook = useFirebaseAuth();

  // Create context value
  const contextValue: AuthContextType = {
    user: authHook.user,
    userProfile: authHook.userProfile,
    loading: authHook.loading,
    error: authHook.error,
    authenticated: authHook.isAuthenticated,

    // Map methods to match expected naming
    login: authHook.signIn,
    register: authHook.signUp,
    signInWithGoogle: authHook.signInWithGoogle,
    logout: authHook.signOut,
    resetPassword: authHook.resetPassword,

    clearError: authHook.clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 *
 * Convenience hook to access auth context.
 * Must be used within AuthProvider.
 *
 * @example
 * ```tsx
 * const { user, authenticated, login, logout } = useAuth();
 * ```
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * AuthWrapper Component
 *
 * Wraps children that require authentication.
 * Redirects to login if user is not authenticated.
 *
 * @example
 * ```tsx
 * <AuthWrapper>
 *   <Dashboard />
 * </AuthWrapper>
 * ```
 */
interface AuthWrapperProps {
  children: ReactNode;
  redirectTo?: string;
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({
  children,
  redirectTo = '/auth'
}) => {
  const { authenticated, loading } = useAuth();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!authenticated) {
    // Store intended destination for redirect after login
    sessionStorage.setItem('intendedDestination', window.location.pathname);

    // Use window.location for immediate redirect to avoid any rendering issues
    if (typeof window !== 'undefined') {
      window.location.href = redirectTo;
    }
    return null;
  }

  // Render children if authenticated
  return <>{children}</>;
};

/**
 * withAuth HOC
 *
 * Higher-order component to inject auth props into a component.
 * Useful for class components or when hooks can't be used.
 *
 * @example
 * ```tsx
 * interface Props {
 *   user: User | null;
 *   authenticated: boolean;
 * }
 *
 * const MyComponent = withAuth<Props>(({ user, authenticated }) => {
 *   return <div>Hello {user?.email}</div>;
 * });
 * ```
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P & { user: User | null; authenticated: boolean }>
) {
  return (props: Omit<P, 'user' | 'authenticated'>) => {
    const auth = useAuth();

    return (
      <AuthWrapper>
        <Component {...props as P} user={auth.user} authenticated={auth.authenticated} />
      </AuthWrapper>
    );
  };
}

export default AuthProvider;
